import React, { useState } from 'react';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { usePreApprovalMetrics } from '../context/PreApprovalMetricsContext';
import { useWiseboardRefresh } from '../context/WiseboardRefreshContext';
import type { DatePreset } from '../types/wiseboard';
import { cn } from '@/lib/utils';

const presets: { label: string; value: DatePreset }[] = [
  { label: 'Today', value: 'today' },
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d' },
  { label: '90 Days', value: '90d' }
];

function getDateRangeForPreset(preset: DatePreset): { start: Date; end: Date } {
  const now = new Date();
  const end = endOfDay(now);

  switch (preset) {
    case 'today':
      return { start: startOfDay(now), end };
    case '7d':
      return { start: startOfDay(subDays(now, 7)), end };
    case '30d':
      return { start: startOfDay(subDays(now, 30)), end };
    case '90d':
      return { start: startOfDay(subDays(now, 90)), end };
    default:
      return { start: startOfDay(subDays(now, 30)), end };
  }
}

export function WiseboardFilterBar() {
  const { dateFilter, setDateFilter, isLoading } = usePreApprovalMetrics();
  const { refreshAll } = useWiseboardRefresh();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handlePresetClick = (preset: DatePreset) => {
    const { start, end } = getDateRangeForPreset(preset);
    setDateFilter({ start, end, preset });
  };

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      await refreshAll();
    } finally {
      setIsRefreshing(false);
    }
  };

  const spinning = isLoading || isRefreshing;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center bg-muted rounded-lg p-1">
        {presets.map((preset) => (
          <Button
            key={preset.value}
            variant="ghost"
            size="sm"
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-all',
              dateFilter.preset === preset.value
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => handlePresetClick(preset.value)}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={handleRefreshAll}
        disabled={spinning}
        className="ml-2"
        aria-label="Refresh all Wiseboard panels"
      >
        <RefreshCw className={cn('h-4 w-4', spinning && 'animate-spin')} />
      </Button>
    </div>
  );
}

export default WiseboardFilterBar;
